import { useHasPermission, useUTLSettingsContext } from "@dexteel/mesf-core";
import { Box } from "@mui/material";
import { get } from "lodash-es";
import { logbookMenuConfig, menuConfigData } from "./menuConfig";
import { NavbarDropdownMenu } from "./NavbarDropdownMenu";
import { NavbarStatusBar } from "./NavbarStatusBar";

/**
 * Converts various value types to boolean.
 * Used for feature flag validation from settings.
 */
function validateBoolean(value: unknown): boolean {
	if (typeof value === "boolean") {
		return value;
	}
	if (typeof value === "string") {
		const lower = value.toLowerCase();
		if (lower === "true" || lower === "1" || lower === "yes") {
			return true;
		}
		if (lower === "false" || lower === "0" || lower === "no") {
			return false;
		}
	}
	if (typeof value === "number") {
		return !!value;
	}
	return false;
}

/**
 * Main navbar component for the RBD Mill application.
 *
 * Layout:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  [Logbook ▼] [Stops ▼]          │ Last Gap │ Speed │ Delays │  │
 * │     (menus)                     │      (status bar)             │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * Features:
 * - Dropdown menus with permission-based visibility
 * - Real-time status bar with delay information
 * - Feature flag support for conditional menu display
 */
export const CustomNavbar = () => {
	const hasPermissionTo = useHasPermission();
	const {
		state: { settings },
	} = useUTLSettingsContext();

	// Feature flags
	const FF_LOGBOOK_ENABLED = validateBoolean(
		get(settings, "FF_LOGBOOK_ENABLED", false),
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
			{/* Menu items on the left */}
			<Box sx={{ display: "flex", alignItems: "center" }}>
				{/* Logbook menu - only if feature flag is enabled */}
				{FF_LOGBOOK_ENABLED && <NavbarDropdownMenu menu={logbookMenuConfig} />}

				{/* Static menus */}
				{menuConfigData.map((menu) => {
					if (menu.permission && !hasPermissionTo(menu.permission)) {
						return null;
					}
					return <NavbarDropdownMenu key={menu.id} menu={menu} />;
				})}
			</Box>

			{/* Status bar items on the right */}
			<NavbarStatusBar />
		</Box>
	);
};
